import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Stethoscope, MessageSquareText, ShieldCheck, PhoneCall } from 'lucide-react';
import { Link } from 'react-router-dom';

// "Let's talk growth." brand lockup (HTML/CSS recreation per design handoff Jul 2026)
const LetsTalkGrowthLockup = () => (
  <div className="select-none" aria-label="Let's talk growth." data-testid="ltg-lockup">
    <div className="flex items-start gap-2.5">
      <span className="font-['Playfair_Display'] font-semibold text-[#F5F1E6] text-[30px] leading-none">Let&rsquo;s</span>
      <span className="relative -mt-1 rounded-[10px] rounded-bl-none bg-[#1CA9BC] px-3 pb-[7px] pt-[5px] font-['Playfair_Display'] font-semibold text-white text-[23px] leading-none">
        talk
        <span className="absolute left-0 -bottom-[8px] h-0 w-0" style={{ borderTop: '9px solid #1CA9BC', borderRight: '10px solid transparent' }} />
      </span>
    </div>
    <div className="mt-[7px] font-['Playfair_Display'] font-semibold text-[#F5F1E6] text-[30px] leading-none">
      growth<span className="text-[#1CA9BC]">.</span>
    </div>
  </div>
);

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const stagger = {
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

// ─── Hero service cards (right column) ────────────────────────────────────────
//
// Outcome-first service showcase (logins moved to /sign-in, Session 61):
//   1. Business Health Check — featured funnel, gold treatment
//   2. Ask a CPA             — the real paid consultation
//   3. IRS Relief            — resolve an IRS problem
//   4. Book a Meeting        — generic intro call (no advisory), direct to CRM

const ctaCards = [
  {
    title: 'Business Valuation & Exit Planning',
    tagline: 'Know your value. Plan your exit.',
    desc: 'Understand what your business is worth and build a plan to grow and harvest that value.',
    lockup: true,
    testid: 'hero-valuation-exit-cta-card',
    link: 'https://letstalkgrowth.com',
    external: true,
    newTab: true,
    primary: true,
  },
  {
    title: 'Check the Health of Your Business',
    tagline: 'Mid-year clarity for business owners.',
    desc: 'Identify financial, tax, operational, and compliance risks before they become year-end surprises.',
    icon: Stethoscope,
    testid: 'hero-bhc-cta-card',
    link: '/business-health-check',
    primary: true,
    footnote: 'Limited engagements this quarter',
  },
  {
    title: 'Ask a CPA',
    tagline: 'A focused session with a licensed CPA.',
    desc: 'Get a professional answer to a tax or business question and walk away with a clear next step.',
    icon: MessageSquareText,
    testid: 'hero-ask-cpa-cta-card',
    link: '/get-your-answer',
  },
  {
    title: 'Resolve an IRS Problem',
    tagline: 'Calm, credible representation.',
    desc: 'Start with an IRS account review. We will see exactly what the IRS has on file and map your next step.',
    icon: ShieldCheck,
    testid: 'hero-irs-relief-cta-card',
    link: '/irs-account-review',
  },
  {
    title: 'Book a Meeting',
    tagline: 'A quick call with our team.',
    desc: 'Not sure where to start? Book a short call and we will point you in the right direction.',
    icon: PhoneCall,
    testid: 'hero-book-meeting-cta-card',
    link: 'https://app.kgob.com/book-consultation?source=home-hero',
    external: true,
    fullWidth: true,
  },
];

export const Hero = () => {
  return (
    <section
      className="relative overflow-hidden bg-[linear-gradient(180deg,#0B2240_0%,#07182F_100%)] text-white noise-overlay"
      data-testid="hero-section"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="lg:grid lg:grid-cols-12 lg:items-center lg:gap-10">
          {/* Left content */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="lg:col-span-6 space-y-6"
          >
            <p className="uppercase tracking-[0.18em] text-xs text-[#E3C96E]" data-testid="hero-tagline">
              QUIET EXCELLENCE
            </p>
            <h1
              className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-[-0.01em] font-semibold"
              data-testid="hero-headline"
            >
              Where Clarity Meets Strategy.
            </h1>
            <p className="text-lg text-white/80 max-w-xl leading-relaxed" data-testid="hero-subheadline">
              Charlotte & Winston-Salem CPAs for tax planning, accounting, and advisory, partner-led service with quiet excellence.
            </p>
            <p className="text-sm italic text-[#E3C96E]/90 max-w-xl" data-testid="hero-brandline">
              Trusted by those who lead, guided by those who care.
            </p>
          </motion.div>

          {/* Right service cards */}
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-3 mt-10 lg:mt-0"
          >
            {ctaCards.map((card) => {
              const Icon = card.icon;
              const isExternal = card.external || card.link.startsWith('http');
              const Wrapper = isExternal ? 'a' : Link;
              const wrapperProps = isExternal
                ? {
                    href: card.link,
                    ...(card.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {}),
                  }
                : { to: card.link };

              return (
                <motion.div
                  key={card.title}
                  variants={fadeUp}
                  whileHover={{ scale: 1.015 }}
                  transition={{ duration: 0.2 }}
                  className={card.fullWidth ? 'sm:col-span-2' : ''}
                >
                  <Wrapper
                    {...wrapperProps}
                    className={`group relative block h-full rounded-2xl border backdrop-blur-xl transition-all duration-300 ${
                      card.primary
                        ? 'p-5 border-[#D4AF37]/60 bg-[linear-gradient(180deg,rgba(212,175,55,0.16)_0%,rgba(212,175,55,0.06)_100%)] hover:border-[#D4AF37] hover:shadow-[0_12px_40px_rgba(212,175,55,0.18)]'
                        : 'p-4 border-white/15 bg-white/10 hover:border-white/30 hover:shadow-[0_12px_40px_rgba(11,34,64,.24)]'
                    }`}
                    data-testid={card.testid}
                  >
                    {/* Featured ribbon for the primary Business Health Check card */}
                    {card.primary && (
                      <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37] px-2.5 py-0.5 text-[9px] tracking-[0.16em] uppercase text-[#0B2240] font-bold shadow-sm">
                        <Sparkles className="w-2.5 h-2.5" />
                        Featured
                      </span>
                    )}

                    <div className="flex items-start justify-between mb-2.5">
                      {card.lockup ? (
                        <LetsTalkGrowthLockup />
                      ) : (
                        <Icon className={card.primary ? 'h-7 w-7 text-[#D4AF37]' : 'h-6 w-6 text-[#D4AF37]'} />
                      )}
                      <ArrowRight className="h-4.5 w-4.5 text-white/60 transition-all duration-200 group-hover:text-white group-hover:translate-x-1" />
                    </div>
                    <h3
                      className={`font-['Playfair_Display'] font-semibold mb-1.5 ${
                        card.primary ? 'text-xl text-[#F7F2E0]' : 'text-lg leading-snug text-white'
                      }`}
                    >
                      {card.title}
                    </h3>
                    <p className={`font-medium mb-1.5 text-[#E3C96E] ${card.primary ? 'text-sm' : 'text-[13px]'}`}>
                      {card.tagline}
                    </p>
                    <p className={`leading-relaxed text-white/70 ${card.primary ? 'text-sm' : 'text-[13px]'}`}>
                      {card.desc}
                    </p>

                    {card.footnote && (
                      <p className="mt-2.5 text-[11px] tracking-[0.14em] uppercase text-[#D4AF37]/80 font-semibold">
                        {card.footnote}
                      </p>
                    )}
                  </Wrapper>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
